import { useLazyQuery } from '@apollo/client';
import { useDealPromptFeatureFlaggedFilters } from '@openx/components-deal-prompt/lib/hooks/useDealPromptFeatureFlaggedFilters.ts';
import { buildSearchQueryInput } from '@openx/components-deal-prompt/lib/utils/buildSearchQueryInput.ts';
import type { UseDealPromptAppsSearchExtendedFetch } from '@openx/components-deal-prompt/lib/utils/context/type.ts';
import { mapCriteriaToFiltersBody } from '@openx/components-deal-prompt/lib/utils/filters/filterMappers.ts';
import { type DealPromptFilters, dealPromptContextTypes } from '@openx/components-deal-prompt/lib/utils/types.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { useCallback } from 'react';
import { InventoryBundleSearchTextDocument } from '../../../graphql/query/dealPrompt/GetInventoryBundleSearchText.generated.tsx';

export const useDealPromptAppsSearchExtendedFetch = (
  criteria: AllTableCriteria<DealPromptFilters>
): UseDealPromptAppsSearchExtendedFetch => {
  const [fetch] = useLazyQuery(InventoryBundleSearchTextDocument, {
    // TODO: EXCH-14467 remove this caching once duplicte id change is merged
    fetchPolicy: 'no-cache',
  });
  const featureFlaggedFilters = useDealPromptFeatureFlaggedFilters();

  const fetchDealPromptAppsExtended = useCallback(
    async ({ query }: { query: string }, abortSignal?: AbortSignal) => {
      const input = {
        query: buildSearchQueryInput({ query }),
        sortBy: 'totalImpressions',
        view: 'desc',
        excludeQuery: [],
        filters: mapCriteriaToFiltersBody(criteria, dealPromptContextTypes.APPS, featureFlaggedFilters),
      };

      const result = await fetch({
        variables: {
          input,
        },
        context: {
          fetchOptions: {
            signal: abortSignal,
          },
        },
      });

      return {
        cumulativeData: result.data?.inventoryBundleSearchText.body.cumulativeData ?? [],
        numberOfResults: result?.data?.inventoryBundleSearchText.body.totalRecords ?? 0,
        inputs: result.data?.inventoryBundleSearchText.body.input ?? {},
        _error: result?.error,
      };
    },
    [fetch, criteria, featureFlaggedFilters]
  );

  return {
    fetchDealPromptAppsExtended,
  };
};
